import { onBeforeUnmount, ref } from 'vue'
import type { Ref } from 'vue'
import type { Instance, AnnotationsUnion, Annotation } from 'pspdfkit'
import type { SaveLinkAnnotationPayload } from '@lib/components/CFileViewer/pdf/CFileViewer.pdf.link.types'
import type { FileRetrieve } from '@openapi'

interface UseOverlayItemsReturnType {
  setOverlayItemsForEmbeddedVideos: (currentPageIndex: number) => Promise<void>
}

export function useOverlayItems({
  fetchFileById,
  instance,
}: {
  fetchFileById?: (id: FileRetrieve['id']) => Promise<FileRetrieve>
  instance: Ref<Instance | undefined>
}): UseOverlayItemsReturnType {
  const CUSTOM_OVERLAY_ITEM_ID_PREFIX = 'embedded-video-'
  const customOverlayItemIds = ref<string[]>([])
  const videoElementsWithListeners = ref<Array<{ videoElement: HTMLVideoElement; handleLoadedData: () => void }>>([])

  async function setOverlayItemsForEmbeddedVideos(currentPageIndex: number) {
    const pageLinkEmbeddedVideoAnnotationUnionArray = await getPageLinkEmbeddedVideoAnnotations(currentPageIndex)
    const annotations = pageLinkEmbeddedVideoAnnotationUnionArray.map((annotationUnion) => annotationUnion.toJS())

    annotations.sort((a, b) => {
      const aAutoplay = a.customData?.autoplay ? 1 : 0
      const bAutoplay = b.customData?.autoplay ? 1 : 0
      return bAutoplay - aAutoplay
    })

    for (const annotation of annotations) {
      await processAnnotation(annotation as Annotation, currentPageIndex)
    }
  }

  async function getPageLinkEmbeddedVideoAnnotations(currentPageIndex: number): Promise<AnnotationsUnion[]> {
    try {
      const pageAnnotations = await instance.value?.getAnnotations(currentPageIndex)

      const pageLinkEmbeddedVideoAnnotations = pageAnnotations?.filter(
        (annotation) =>
          annotation instanceof window.PSPDFKit.Annotations.LinkAnnotation && annotation.customData?.embedded,
      )

      return pageLinkEmbeddedVideoAnnotations ? Array.from(pageLinkEmbeddedVideoAnnotations) : []
    } catch (e) {
      console.error('[useAnnotations.ts] getPageLinkEmbeddedVideoAnnotations failed: ', e)
      return []
    }
  }

  async function processAnnotation(annotation: Annotation, currentPageIndex: number) {
    const customData = annotation.customData as SaveLinkAnnotationPayload['customData']
    const customOverlayItemId = `${CUSTOM_OVERLAY_ITEM_ID_PREFIX}${annotation.id}`

    if (customOverlayItemIds.value.includes(customOverlayItemId)) return

    const videoWidget = createVideoWidget(annotation)
    const item = createCustomOverlayItem({ videoWidget, customOverlayItemId, currentPageIndex, annotation })

    instance.value?.setCustomOverlayItem(item)
    await setVideoSource(videoWidget, customData)

    customOverlayItemIds.value.push(customOverlayItemId)
  }

  function createVideoWidget(annotation: Annotation) {
    const videoWidget = document.createElement('div')
    videoWidget.innerHTML = `
    <video width="${annotation.boundingBox!.width}" height="${annotation.boundingBox!.height}" controls>
      <source src="" type="video/mp4">
      Your browser does not support HTML5 video.
    </video>
  `
    return videoWidget
  }

  function createCustomOverlayItem({
    videoWidget,
    customOverlayItemId,
    currentPageIndex,
    annotation,
  }: {
    videoWidget: HTMLDivElement
    customOverlayItemId: string
    currentPageIndex: number
    annotation: Annotation
  }) {
    return new window.PSPDFKit.CustomOverlayItem({
      id: customOverlayItemId,
      node: videoWidget,
      pageIndex: currentPageIndex,
      position: new window.PSPDFKit.Geometry.Point({
        x: annotation.boundingBox!.left,
        y: annotation.boundingBox!.top,
      }),
    })
  }

  async function setVideoSource(videoWidget: HTMLDivElement, customData: SaveLinkAnnotationPayload['customData']) {
    if (!customData.fileId) return

    const file = await fetchFileById?.(customData.fileId)
    const videoElement = videoWidget.querySelector('video')
    if (videoElement) {
      const sourceElement = videoElement.querySelector('source')
      if (sourceElement && file?.content_url) {
        sourceElement.src = file.content_url
        videoElement.load()
        setupVideoEvents(videoElement, customData)
      } else {
        console.error('File content URL is null or source element not found')
      }
    }
  }

  function setupVideoEvents(videoElement: HTMLVideoElement, customData: SaveLinkAnnotationPayload['customData']) {
    const handleLoadedData = () => {
      videoElement.currentTime = customData.startTime ?? 0
      if (customData.autoplay) {
        tryAutoplayVideo(videoElement)
      }
    }
    videoElement.addEventListener('loadeddata', handleLoadedData)
    videoElementsWithListeners.value.push({ videoElement, handleLoadedData }) // Keep track of the video element and its listener
  }

  async function tryAutoplayVideo(videoElement: HTMLVideoElement) {
    try {
      await videoElement.play()
    } catch (error) {
      console.error('Error attempting to play video:', error)
      // If autoplay fails (when there is no previous user interaction), mute the video and try to play again
      videoElement.muted = true
      videoElement.play().catch((muteError) => {
        console.error('Error attempting to play muted video:', muteError)
      })
    }
  }

  function cleanup() {
    for (const { videoElement, handleLoadedData } of videoElementsWithListeners.value) {
      videoElement.removeEventListener('loadeddata', handleLoadedData)
    }
    videoElementsWithListeners.value = [] // Clear the references
  }

  onBeforeUnmount(() => {
    cleanup()
  })

  return {
    setOverlayItemsForEmbeddedVideos,
  }
}
